/**
 * @fileoverview Client context for experiment exposure tracking.
 */

"use client"

import { createContext, useContext, type ReactNode } from "react"

import type { ExperimentAssignment } from "./dimensions"

const ExperimentExposureContext = createContext<readonly ExperimentAssignment[]>([])

/**
 * Provides active experiment assignments to client descendants.
 *
 * @param {Object} props - Component props.
 * @param {readonly ExperimentAssignment[]} props.assignments - Server-evaluated experiments.
 * @param {ReactNode} props.children - Child tree.
 * @returns {ReactNode} Context provider.
 */
export function ExperimentExposureProvider({
  assignments,
  children,
}: {
  assignments: readonly ExperimentAssignment[]
  children: ReactNode
}): ReactNode {
  return (
    <ExperimentExposureContext.Provider value={assignments}>
      {children}
    </ExperimentExposureContext.Provider>
  )
}

/**
 * Reads active experiment assignments from context.
 *
 * @returns {readonly ExperimentAssignment[]} Assignments for the current request.
 */
export function useExperimentAssignments(): readonly ExperimentAssignment[] {
  return useContext(ExperimentExposureContext)
}

/**
 * Reads one experiment's assigned variant from context.
 *
 * @param {string} experimentKey - Registry key.
 * @returns {string | undefined} Variant id when assigned.
 */
export function useExperimentVariant(experimentKey: string): string | undefined {
  return useExperimentAssignments().find((a) => a.experiment_id === experimentKey)?.variant_id
}
